/**
 * The two currency decisions the SPA makes: what a stored code normalizes to, and which
 * code a screenful of aggregated figures is labelled with.
 *
 * `currencyCode` moved to `@steward-app/steward-types` (steward-app#484) and is re-exported
 * here so the SPA call sites keep their short local import.
 *
 * It moved because it stopped being a rendering concern only: `services/snapshot-calculator`
 * now labels each net-worth snapshot with the currency of the holdings it summed, using this
 * same normalizer. The two MUST apply the same rule — a calculator that wrote a code this
 * function rejects would have the SPA quietly substitute a fallback, which is the shape of
 * bug #484 fixed.
 *
 * `statementCurrency` stays SPA-only (nothing outside the browser renders a statement) but
 * had to leave `src/pages/Portfolio.tsx`, because the Dashboard hero needs the identical
 * answer from the identical inputs and a second implementation there would be a second
 * chance to disagree (steward-app#478).
 *
 * `fmt$` moved here from `src/pages/portfolio/forms.tsx` (steward-app#1231) and is re-exported
 * there, so every existing call site keeps its import. It moved for the reason `cls` moved to
 * `src/components/formStyles.ts`: the public intake page renders money now (the confirmation
 * screen's receipt of what a prospect connected), and `forms.tsx` pulls the API client and the
 * auth hook, neither of which belongs in a pre-auth bundle. This module's only runtime import is
 * `@steward-app/steward-types`; the `api/types` import below is types-only and erases.
 *
 * `formatCompactMoney` and `formatWholeMoney` (steward-app#638) resolve a THIRD formatter
 * that had been copied page to page under the guise of two functions with different names.
 * Dashboard's `formatCompact` and AdvisorClients' `formatMoney` were byte-identical in
 * observable output (`notation: 'compact', maximumFractionDigits: 1`, and the same #479
 * guard comment travelling with each copy) — those two merged into `formatCompactMoney`
 * with no change to what either page renders. Entities' `fmt$` looked like a third copy of
 * the same thing (same guard comment, same `currencyCode(...) ?? 'USD'` fallback line) but
 * was not: it has no `notation: 'compact'` at all — it renders WHOLE figures
 * (`minimumFractionDigits: 0, maximumFractionDigits: 0`) and has an `undefined` ->
 * "Price unavailable" branch neither of the other two had. That formatter is real and
 * shared-worthy on its own terms, just not the same one — it moved here as
 * `formatWholeMoney`, byte-identical in behaviour, so Entities.tsx now imports it instead
 * of defining it locally. Moving all three (rather than extracting two and renaming the
 * third in place) is deliberate: leaving one formatter page-local is exactly the condition
 * that let the next page copy the nearest-looking function, which is how this module ended
 * up needing #638 in the first place.
 *
 * The naming is inconsistent on purpose rather than by oversight: `fmt$` keeps its original
 * name (moved from `forms.tsx` two hours before `formatCompactMoney`/`formatWholeMoney` were
 * added, with call sites and a re-export this change does not own) while the two newer
 * exports spell out `format...Money`. Left as a known leftover rather than a cross-cutting
 * rename that would touch code outside this issue's scope.
 */
import type { Resource, AssetAttributes, AccountAttributes, LiabilityAttributes } from '../api/types';
import { currencyCode } from '@steward-app/steward-types';

export { currencyCode };

/**
 * `amount` is `undefined` only when the underlying price/value is genuinely
 * missing (e.g. a new equity before its first EOD close, or a live-quote
 * fetch that failed) — never a real $0 holding, which always carries an
 * actual `0`. Showing "Price unavailable" here (rather than $0, or a dash)
 * keeps "we don't know" visibly distinct from "this is worth nothing".
 *
 * ⚠️ That `undefined` wording is a PORTFOLIO sentence, and a caller outside the portfolio
 * surfaces should say its own thing rather than pass `undefined` in and inherit it. The intake
 * receipt does exactly that: a snapshot balance of `null` means the bank reported none, which
 * is not a price that failed to load.
 */
export function fmt$(amount: number | undefined, currency = 'USD') {
  if (amount === undefined) return 'Price unavailable';
  // Guarded: a stored currency of '' or 'US' reaches Intl unchanged otherwise, and Intl
  // throws a RangeError on anything that is not a well-formed three-letter code — during
  // render, which blanks the whole page. Falling back to USD degrades this one row's
  // formatting instead of crashing the page (steward-app#479).
  const code = currencyCode(currency) ?? 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

/**
 * `v` is `undefined` only for a genuinely-missing asset price (never a real $0 holding) —
 * "Price unavailable" keeps that distinct from an actual zero.
 *
 * ⚠️ That `undefined` wording was written for Entities.tsx (its sole caller when this moved
 * here, steward-app#638) and is exactly the same hazard `fmt$` above already carries: a
 * caller outside the context "Price unavailable" was written for should say its own thing
 * rather than pass `undefined` in and inherit it.
 */
export function formatWholeMoney(v: number | undefined, currency = 'USD') {
  if (v === undefined) return 'Price unavailable';
  // Guarded: a stored currency of '' or 'US' reaches Intl unchanged otherwise, and Intl
  // throws a RangeError on anything that is not a well-formed three-letter code — during
  // render, which blanks the whole page. Falling back to USD degrades this one row's
  // formatting instead of crashing the page (steward-app#479).
  const code = currencyCode(currency) ?? 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
}

/**
 * Compact currency (`$1.2M`, `$450K`) — for a headline figure or a card total where the
 * exact cents would crowd the layout, not for anything a family needs to reconcile against
 * a statement.
 *
 * Extracted (steward-app#638) from Dashboard's `formatCompact` and AdvisorClients'
 * `formatMoney`, which had drifted into two page-local copies of the same body. Kept OUT of
 * this extraction: `formatWholeMoney` above, which is not this formatter — see the module
 * docblock for the character-level diff that decided that.
 */
export function formatCompactMoney(amount: number, currency?: string) {
  // Guarded: a stored currency of '' or 'US' reaches Intl unchanged otherwise, and Intl
  // throws a RangeError on anything that is not a well-formed three-letter code — during
  // render, which blanks the whole page. Falling back to USD degrades this one figure's
  // formatting instead of crashing the page (steward-app#479).
  const code = currencyCode(currency) ?? 'USD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, notation: 'compact', maximumFractionDigits: 1 }).format(amount);
}

/**
 * The currency to format a household's AGGREGATE figures in (steward-app#474).
 *
 * Two surfaces call this, and they must agree because they show the same number: the
 * Portfolio statement (headline, totals, every group / entity / institution subtotal, and
 * the Performance panel's range badges) and the Dashboard hero (net worth, the assets and
 * liabilities line, the trend chip, the allocation strip). One function, one set of inputs,
 * so the two cannot label the same money differently (steward-app#478).
 *
 * Same shape as #467, one field over. Everything both surfaces sum is summed live, this
 * render, from the ['assets'] and ['liabilities'] queries; the currency used to come from
 * the latest NET-WORTH SNAPSHOT, a different source on a different clock, with `'USD'`
 * behind it when there was no snapshot at all. A household that has added holdings but not
 * yet been through a nightly run has exactly that: no snapshot. So a family holding GBP or
 * CHF read their net worth, their totals and every group subtotal with a `$` in front, and
 * nothing on the page said otherwise. It corrected itself after the first EOD run, which
 * makes it a FIRST-SESSION bug specifically.
 *
 * The Dashboard's own version of the bug survived #474 by a further step: behind the
 * snapshot it read `positions[0]`, so whichever holding the API happened to return first
 * decided the household's headline currency. That is the insertion-order dependence this
 * function exists to remove, and #478 deleted it.
 *
 * The figures decide their own currency. Weighting is by the same numbers the totals sum —
 * an asset's `currentValue`, a liability's `currentBalance` — so the currency agrees with
 * what the totals are mostly made of rather than with how many rows exist. Absolute value,
 * because liabilities enter the net figure negative and a large debt is still a large
 * amount of its currency.
 *
 * "The figures" is the FULL set, deliberately: on Portfolio this one value also labels the
 * filtered subtotals and the By Owner / By Institution groups, so a group holding nothing
 * in the dominant currency is still labelled with it. That is the better of two imperfect
 * options, because a symbol that flipped as a filter checkbox was ticked would read as the
 * money having changed rather than the view. It is NOT a claim that either page renders one
 * currency: row-level sites still format from their own stored code (every
 * `fmt$(…, lib.attributes.currency)` and the two account ones on Portfolio, the largest-
 * holdings list on the Dashboard), so a sterling household with a dollar account really
 * does see a `$` figure inside that account's row. Whether those should be converted,
 * labelled or left alone is steward-app#479.
 *
 * Accounts are weighed by nothing, and counted only when they hold nothing. One principle
 * on two axes: no vote may be cast twice for the same money. Weighing an account would
 * double-count outright, because its value on the statement IS the positions inside it.
 * Counting a NON-EMPTY one does the same thing one axis over — `holdings` is a ranking
 * dimension, so an account whose contents have already voted buys their currency a second
 * vote. A workspace with one unpriced dollar asset inside a dollar account and one unpriced
 * sterling asset sitting loose is perfectly symmetric in what it holds, and would read as a
 * dollar household on nothing but where a row happens to sit.
 *
 * An account holding nothing has no such conflict, and it is the case worth having: a
 * family who create their GBP account on day one, before entering a single holding, would
 * otherwise be shown $0 in exactly the first session this fix is about. "Holding nothing"
 * means no positions AND no liabilities — a paid-off card sits inside an account and votes
 * with its own currency, so that account must not vote again either.
 *
 * Deterministic by construction, since two renders of the same data must not disagree:
 * greatest summed magnitude first, then the most rows counted (which is what settles a
 * workspace with nothing priced yet — a new equity before its first close, or that day-one
 * account, contributes no magnitude but is still evidence of the currency the family
 * holds), then the code itself, ascending. Never insertion order, which is what a bare
 * `Map` iteration would have made it.
 *
 * One honest qualification on that: the input to the tiebreak includes rows carrying no
 * money, so adding or removing an EMPTY account can flip the page's currency where two
 * currencies are exactly tied on magnitude and count. A tie has no right answer and
 * neither outcome is wrong, but "determined by the data" is doing slightly more work than
 * "determined by the figures" would.
 *
 * MIXED currencies are aggregated as if they were one, which is what both pages have always
 * done: totals add the numbers whatever their currency. Picking the dominant one makes the
 * label true of most of the sum instead of true of nothing. Whether a genuinely
 * multi-currency household should be aggregated at all is an open question this does not
 * settle (steward-app#474).
 *
 * The snapshot is a defensive tail, and it is only now becoming a signal. It used to carry
 * no household currency at all: `services/snapshot-calculator/src/calculator.ts` wrote
 * `currency: 'USD'` as a literal, unconditionally, and nothing else writes those rows, so
 * reading it could only ever return the same answer as the 'USD' behind it. steward-app#484
 * fixed the writer — a snapshot now records the currency of the holdings it summed, and
 * omits the field entirely rather than guessing when nothing resolvable voted, which is why
 * `currencyCode(undefined)` on that line is a real case and not defensive noise.
 *
 * The ORDER stays as it is regardless. Live holdings are what these figures are summed
 * from, this render; the snapshot is a different source on a different clock, and every row
 * written before #484 still says 'USD' whatever the household holds (steward-app#515). It
 * reaches this tail only when the live holdings named no usable currency at all.
 *
 * The return is always a code `Intl.NumberFormat` accepts, never a raw stored string: every
 * candidate passes through `currencyCode` and the final `'USD'` is the floor. A caller can
 * hand the result straight to `Intl` without re-guarding it (steward-app#479).
 */
export function statementCurrency(
  positions: Resource<AssetAttributes>[],
  accounts: Resource<AccountAttributes>[],
  liabilities: Resource<LiabilityAttributes>[],
  snapshotCurrency: string | undefined,
): string {
  const weights = new Map<string, { magnitude: number; holdings: number }>();

  /** `figure: undefined` = counts toward the tiebreak, contributes no magnitude. */
  function weigh(raw: string | undefined, figure: number | undefined) {
    const code = currencyCode(raw);
    if (!code) return;
    const entry = weights.get(code) ?? { magnitude: 0, holdings: 0 };
    // Guarded rather than added blind: storage validates nothing, and one NaN would
    // poison every comparison below (NaN > x and NaN < x are both false), which decides
    // the currency by sort order instead of by the data.
    if (typeof figure === 'number' && Number.isFinite(figure)) entry.magnitude += Math.abs(figure);
    entry.holdings += 1;
    weights.set(code, entry);
  }

  for (const position of positions) weigh(position.attributes.currency, position.attributes.currentValue);
  for (const liability of liabilities) weigh(liability.attributes.currency, liability.attributes.currentBalance);

  // An account votes only when nothing inside it has already voted, and then on the
  // tiebreak alone. See the docblock: one anti-double-count rule, applied to both axes.
  const occupied = new Set<string>();
  for (const position of positions) if (position.attributes.accountId) occupied.add(position.attributes.accountId);
  for (const liability of liabilities) if (liability.attributes.accountId) occupied.add(liability.attributes.accountId);
  for (const account of accounts) {
    if (occupied.has(account.id)) continue;
    weigh(account.attributes.currency, undefined);
  }

  const ranked = Array.from(weights.entries()).sort(([aCode, a], [bCode, b]) =>
    b.magnitude - a.magnitude
    || b.holdings - a.holdings
    // Not `localeCompare`: these are ASCII codes, and the runner's locale must not be
    // able to change which currency a statement renders in.
    || (aCode < bCode ? -1 : aCode > bCode ? 1 : 0),
  );

  return ranked[0]?.[0] ?? currencyCode(snapshotCurrency) ?? 'USD';
}
